import { isServer, MutationCache, QueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

const makeQueryClient = () => {
  const queryClient = new QueryClient({
    defaultOptions: {
      mutations: {
        meta: {
          toastOnError: true,
          toastOnSuccess: true
        }
      },
      queries: {
        staleTime: 60 * 1000 // 1 minute
      }
    },
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (isServer) return
        if (mutation.meta?.toastOnError === false) return

        if (mutation.meta?.errorMessage) {
          toast.error(mutation.meta?.errorMessage)
        } else {
          toast.error(error.response?.data.message || error.message)
        }
      },

      onSettled: async (_data, _error, _variables, _context, mutation) => {
        if (!mutation.meta?.invalidatesQuery) return

        // Check for special 'all' key to invalidate everything
        if (
          mutation.meta.invalidatesQuery.some(
            (queryKey) => queryKey[0] === 'all'
          )
        ) {
          await queryClient.invalidateQueries()
          return
        }

        // Invalidate each query key separately
        await Promise.all(
          mutation.meta.invalidatesQuery.map((queryKey) =>
            queryClient.invalidateQueries({
              exact: false,
              queryKey
            })
          )
        )
      },
      onSuccess: (data, _variables, _context, mutation) => {
        if (isServer) return
        if (mutation.meta?.toastOnSuccess === false) return

        if (mutation.meta?.successMessage) {
          toast.success(mutation.meta?.successMessage)
        } else if (
          data &&
          typeof data === 'object' &&
          'message' in data &&
          typeof data.message === 'string'
        ) {
          toast.success(data.message)
        }
      }
    })
  })

  return queryClient
}

let browserQueryClient: QueryClient | undefined = undefined

export function getQueryClient() {
  if (isServer) {
    return makeQueryClient()
  } else {
    if (!browserQueryClient) browserQueryClient = makeQueryClient()
    return browserQueryClient
  }
}
